/**
 * UC3 — Agricultural Productivity Diagnosis, maize (design §3.3, Amendment 1).
 *
 * A stepper (`Stepper.tsx`) over machinery this codebase already has: the geography/scope
 * cascade, the catalogue's own raster epochs, and the `conditions` engine tool (Task 5)
 * submitted through the same `useToolRun` every §F6 dock form uses. Local state carries the
 * reader's choices — none of it is permalinked, the same rule `intel.*` is already under
 * (design §6.3) — seeded once from the store's own country/scope so a reader who has
 * already filtered the map to a country or a watershed opens the stepper already pointed
 * there, without this stepper ever writing back to those global controls (the same one-way
 * seeding `Change.tsx`'s `initialExtent` uses for its own extent pick).
 *
 * Amendment 1 §2.3/§3.3: the step list itself comes from `GET uc3/definition`'s own
 * `definition.steps` (order, ids, kinds) rather than a hardcoded array, so a future
 * definition edit (reordering, adding/removing a step) needs no frontend change — only the
 * `aeti` step is folded into the rainfall step as its own optional toggle (`#uc3Aeti`)
 * rather than consuming a step number of its own. `#uc3Step-<n>` still numbers the steps
 * 1..N in the order the definition lists them.
 *
 * UC3 launch updates (client, 8 Oct 2026): Year comes before Crop; the Geography step
 * isolates the map to the chosen country or watershed (`intel.isolate`, drawn and used as
 * the raster mask by `features/map/IntelIsolate.tsx`) and fits the view to it; the Crop and
 * Productivity steps put the crop's own layers on the map (`uc3Layers.ts`), with
 * `#uc3Measure` switching Productivity between the survey yield terciles and the modelled
 * GAEZ layers for reference only; the Vegetation step has no swipe; the Results panel
 * carries no limitations block.
 *
 * Production total and flow layers (client, 8 Oct 2026, NOTES #753): every painting step
 * adds its layer to `intel.uc3Layers` and leaves the earlier ones on the map (drawn by
 * `RasterLayers`/`ResultLayers`, toggled from the legend); changing a step's choice replaces
 * its layer in place. The Productivity step's `#uc3ProductionTotal` gives the selected
 * unit's modelled 2020 production (the diagnosis payload's `production.sums`) beside its
 * survey yield and tercile.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { useToolRun, type ConditionClause, type ConditionsResult } from '../../lib/analysis';
import {
  fetchUnitDetail,
  fetchUnitOptions,
  fetchUnits,
  queryKeys,
  type IntelDiagnosis,
  type IntelSeries,
  type IntelStepDefinitionRow,
  type IntelUc3Crop,
} from '../../lib/api';
import { ChartFrame } from '../../components/charts/ChartFrame';
import { axisTitle } from '../../components/charts/scale';
import { fmtCompact, fmtNum } from '../../lib/format';
import {
  COUNTRY_NAMES,
  PROGRAMME_BOUNDS,
  useAppStore,
  WATERSHED_LEVEL,
  type Country,
  type IntelIsolate,
} from '../../lib/state';
import { epochDate, epochsForYear } from '../map/epochs';
import { useAnalysisLayers } from '../analysis/pickers';
import { ConditionBuilder } from './ConditionBuilder';
import { Insight } from './Insight';
import { Stepper, type StepperStep } from './Stepper';
import { HistogramChart } from './charts/HistogramChart';
import { RankedBarChart } from './charts/RankedBarChart';
import { StatTile, StatTiles } from './charts/StatTile';
import { SeriesChart } from './charts/SeriesChart';
import { useIntelDefinition, useIntelDiagnosis, useIntelSeries, useIntelUc3Config } from './api';
import { useUnitLink } from './useUnitLink';
import {
  rasterForStep,
  TERCILE_COLORS,
  TERCILES,
  UC3_MEASURES,
  yieldTerciles,
  type Uc3Measure,
} from './uc3Layers';

/** The four real countries UC3 can run against — `conditions.py::_country` refuses "ALL". */
const UC3_COUNTRIES = (Object.keys(COUNTRY_NAMES) as Country[]).filter((c) => c !== 'ALL');

/** The productivity map's own result layer id. */
const PRODUCTIVITY_LAYER = 'intel:uc3:productivity';

/** The steps whose layers this component writes to the flow list (Analyse writes its own). */
const PAINTING_STEPS = ['crop', 'indicator', 'ndvi', 'rainfall', 'soil'] as const;

const SOIL_LAYERS = ['soc', 'sgn', 'sgcec', 'ph'] as const;
type SoilLayer = (typeof SOIL_LAYERS)[number];

const DIAGNOSIS_FACTORS = ['ndvi_peak', 'rain', 'soc'] as const;

/** `uc3/diagnosis`'s own factor units — presentation standard §1's "numbers with unit". */
const DIAGNOSIS_UNITS: Record<'yield' | 'modelled_yield' | (typeof DIAGNOSIS_FACTORS)[number], string> = {
  yield: 'kg/ha',
  modelled_yield: 't/ha',
  ndvi_peak: 'NDVI',
  rain: 'mm',
  soc: 'g/kg',
};

/**
 * GAEZ round: `uc3/definition`'s own `definition.crops`/`uc3/config`'s own `crops` — kept in
 * sync server-side (Task 2's own report) — used before either has resolved, the same role
 * `FALLBACK_STEPS` plays for the step list.
 */
const FALLBACK_CROPS: IntelUc3Crop[] = [
  { id: 'maize', label: { en: 'Maize', fr: 'Maïs' }, survey: 'mb_yield_maize', mask: 'mask_maize', gaez: 'gaez_yld_maize' },
  { id: 'rice', label: { en: 'Rice', fr: 'Riz' }, survey: 'mb_yield_rice', mask: 'mask_rice', gaez: 'gaez_yld_rice' },
  { id: 'soy', label: { en: 'Soybean', fr: 'Soja' }, survey: 'mb_yield_soybean', mask: 'mask_soy', gaez: 'gaez_yld_soy' },
  { id: 'sorghum', label: { en: 'Sorghum', fr: 'Sorgho' }, survey: 'mb_yield_sorghum', mask: null, gaez: 'gaez_yld_sorghum' },
  { id: 'millet', label: { en: 'Millet', fr: 'Mil' }, survey: 'mb_yield_millet', mask: null, gaez: 'gaez_yld_millet' },
];

/**
 * Fix round 1: a defensive display filter — no ρ/correlation term reaches the reader even
 * from a stale cache or a payload a future backend round mis-shapes, on top of the design
 * guarantee that the server never sends one. Matches the same four terms
 * `vocabulary.test.ts` sweeps the locale files for.
 */
function hasForbiddenStatsTerm(text: string): boolean {
  if (text.includes('ρ')) return true;
  return /\b(rho|correlation|corrélation)\b/i.test(text);
}

/** Drops any statement line carrying a forbidden term, warning once per dropped line. */
function safeStatements(lines: string[] | undefined): string[] {
  if (!lines) return [];
  return lines.filter((line) => {
    if (hasForbiddenStatsTerm(line)) {
      console.warn('[uc3] dropped a diagnosis statement carrying a forbidden correlation term', line);
      return false;
    }
    return true;
  });
}

/** Same filter for the single insight paragraph, per language, falling back to the empty state. */
function safeInsightText(text: string | undefined, fallback: string): string {
  if (!text) return fallback;
  if (hasForbiddenStatsTerm(text)) {
    console.warn('[uc3] dropped a diagnosis insight carrying a forbidden correlation term', text);
    return fallback;
  }
  return text;
}

/**
 * The stepper's own fallback order — used only while `uc3/definition` has not resolved yet,
 * so the pane still renders something on first paint. Identical in shape to the live
 * `uc3/definition.steps` (`aeti` folded into `rainfall`, `results` last).
 */
const FALLBACK_STEPS: IntelStepDefinitionRow[] = [
  { id: 'geography', kind: 'geography', binding: { levels: ['admin0', 'watershed'] } },
  { id: 'year', kind: 'year', binding: {} },
  { id: 'crop', kind: 'crop', binding: {} },
  { id: 'indicator', kind: 'indicator', binding: { indicator: 'mb_yield_maize' } },
  { id: 'ndvi', kind: 'raster', binding: { layer: 'ndvi_terra' } },
  {
    id: 'rainfall',
    kind: 'raster',
    binding: { layer: 'rainfall', policy: 'dekads_else_annual', datasets: { dekad: 'chirps', annual: 'chirpsy' } },
  },
  { id: 'aeti', kind: 'raster', binding: { layer: 'aeti', period: 2025 }, optional: true },
  { id: 'soil', kind: 'soil_selector', binding: { layers: ['soc', 'sgcec', 'ph'] } },
  { id: 'conditions', kind: 'conditions', binding: {} },
  { id: 'results', kind: 'results', binding: { ranked: true, correlations: false } },
];

function uniqSorted(values: number[]): number[] {
  return [...new Set(values.filter((v) => Number.isFinite(v)))].sort((a, b) => a - b);
}

function yearOf(date: string): number {
  return Number(date.slice(0, 4));
}

function langOf(language: string): 'en' | 'fr' {
  return language === 'fr' ? 'fr' : 'en';
}

export function Uc3() {
  const { t, i18n } = useTranslation();
  const lang = langOf(i18n.language);
  const step = useAppStore((s) => s.intel.step);
  const setStep = useAppStore((s) => s.setIntelStep);
  const storeCountry = useAppStore((s) => s.country);
  const storeScope = useAppStore((s) => s.scope);
  const layerOpacity = useAppStore((s) => s.layerOpacity);
  const setLayerOpacity = useAppStore((s) => s.setLayerOpacity);
  const setEpoch = useAppStore((s) => s.setEpoch);
  const addResultLayer = useAppStore((s) => s.addResultLayer);
  const removeResultLayer = useAppStore((s) => s.removeResultLayer);
  const setIntelIsolate = useAppStore((s) => s.setIntelIsolate);
  const flowLayers = useAppStore((s) => s.intel.uc3Layers);
  const setUc3StepLayers = useAppStore((s) => s.setUc3StepLayers);
  const clearUc3Layers = useAppStore((s) => s.clearUc3Layers);
  const setReportingLevel = useAppStore((s) => s.setReportingLevel);
  const fitBounds = useAppStore((s) => s.fitBounds);
  const qc = useQueryClient();

  // The productivity map paints watersheds, so the map's units are the watershed ones
  // (the same assertion UC1 and UC2 make for their own level).
  useEffect(() => {
    setReportingLevel(WATERSHED_LEVEL);
  }, [setReportingLevel]);

  useEffect(() => {
    if (step === 0) setStep(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const active = step || 1;

  const definitionQuery = useIntelDefinition('uc3');
  // `aeti` is a toggle inside the rainfall step, not a step of its own — see module doc.
  const stepDefs = useMemo(
    () => (definitionQuery.data?.definition.steps ?? FALLBACK_STEPS).filter((s) => s.id !== 'aeti'),
    [definitionQuery.data],
  );
  const aetiDef = (definitionQuery.data?.definition.steps ?? FALLBACK_STEPS).find((s) => s.id === 'aeti');
  const stepNumberOf = (id: string): number => {
    const i = stepDefs.findIndex((s) => s.id === id);
    return i === -1 ? 0 : i + 1;
  };
  const rainfallDef = stepDefs.find((s) => s.id === 'rainfall');

  // `'ALL'` is "All countries": the whole programme, nothing isolated on the map.
  const [country, setCountry] = useState<Country>(() => storeCountry);
  const [scopeUnit, setScopeUnit] = useState<string>(() =>
    storeScope?.unitType === 'watershed' ? storeScope.unitId : '',
  );

  const watersheds = useQuery({
    queryKey: ['uc3-watersheds', country],
    queryFn: () => fetchUnitOptions('watershed', null, country as Country, null, false),
  });

  /* ---- geography: isolate the map to the selection and fit to it ---- */
  // The four level-0 outlines, on `GeographyFocus`'s own cache key: the country's unit id.
  const admin0 = useQuery({
    queryKey: queryKeys.units('admin', 0, 'ALL', 'z7'),
    queryFn: () => fetchUnits('admin', 0, 'ALL', 'z7'),
  });
  const countryUnitId =
    country === 'ALL' ? null : (admin0.data?.features.find((f) => f.properties.country === country)?.properties.id ?? null);
  const isolate: IntelIsolate | null = scopeUnit
    ? { unitId: scopeUnit, level: 'watershed', country }
    : country !== 'ALL'
      ? { unitId: countryUnitId ?? country, level: 'admin0', country }
      : null;
  useEffect(() => {
    setIntelIsolate(isolate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isolate?.unitId, isolate?.level, isolate?.country]);
  useEffect(() => () => setIntelIsolate(null), [setIntelIsolate]);

  /**
   * The same fit `#intelFocus` makes through `useUnitLink.pick`: the unit's own
   * `/hierarchy/units/{id}.bbox`, handed to `fitBounds`. Only on a *change* of geography,
   * never on opening the stepper (the map is already where the reader left it), and back
   * to the programme extent when the selection is cleared.
   */
  const geographyKey = `${country}|${scopeUnit}`;
  const fittedGeography = useRef(geographyKey);
  useEffect(() => {
    if (fittedGeography.current === geographyKey) return;
    const target = scopeUnit || countryUnitId;
    if (!target) {
      if (country === 'ALL') {
        fittedGeography.current = geographyKey;
        fitBounds(PROGRAMME_BOUNDS);
      }
      return;
    }
    fittedGeography.current = geographyKey;
    void qc
      .fetchQuery({ queryKey: queryKeys.unit(target), queryFn: () => fetchUnitDetail(target) })
      .then((detail) => {
        if (fittedGeography.current !== geographyKey || !detail.bbox) return;
        const [minx, miny, maxx, maxy] = detail.bbox;
        fitBounds([
          [miny, minx],
          [maxy, maxx],
        ]);
      })
      .catch(() => {
        /* no geometry for this unit: the isolation still stands, only the fit does not fire */
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geographyKey, countryUnitId]);

  const config = useIntelUc3Config();
  /**
   * GAEZ round: `definition.crops` is the ruling's own source for the crop select — falls
   * back to `uc3/config`'s own `crops` (kept in sync server-side) while the definition has
   * not resolved yet, then to the hardcoded fallback.
   */
  const crops = definitionQuery.data?.definition.crops ?? config.data?.crops ?? FALLBACK_CROPS;
  const [crop, setCrop] = useState<string>('maize');
  useEffect(() => {
    if (crops.length && !crops.some((c) => c.id === crop)) setCrop(crops[0]!.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [definitionQuery.data, config.data]);
  const cropDef = crops.find((c) => c.id === crop) ?? crops[0];
  const cropLabel = cropDef ? cropDef.label[lang] : crop;

  const layers = useAnalysisLayers();
  const ndviSeries = layers.find((l) => l.id === 'ndvi_terra') ?? null;
  const ndviYears = useMemo(
    () => uniqSorted((ndviSeries?.epochs ?? []).map((e) => yearOf(epochDate(e)))),
    [ndviSeries],
  );

  /* ---- rainfall step: dekad (`chirps`) when the season's dekads exist, else annual (`chirpsy`) ---- */
  const dekadLayerId = rainfallDef?.binding.datasets?.dekad ?? 'chirps';
  const annualLayerId = rainfallDef?.binding.datasets?.annual ?? 'chirpsy';
  const dekadSeries = layers.find((l) => l.id === dekadLayerId) ?? null;
  const annualSeries = layers.find((l) => l.id === annualLayerId) ?? null;
  const dekadYears = useMemo(
    () => uniqSorted((dekadSeries?.epochs ?? []).map((e) => yearOf(epochDate(e)))),
    [dekadSeries],
  );
  const annualYears = useMemo(
    () => uniqSorted((annualSeries?.epochs ?? []).map((e) => yearOf(epochDate(e)))),
    [annualSeries],
  );

  /**
   * Years both `ndvi_terra` and the annual rainfall layer can answer for — falling back to
   * `ndvi_terra` alone when the rainfall archive has published none yet or the two series
   * simply do not overlap, rather than offering the reader an empty select.
   */
  const availableYears = useMemo(() => {
    if (!annualYears.length) return ndviYears;
    const intersection = ndviYears.filter((y) => annualYears.includes(y));
    return intersection.length ? intersection : ndviYears;
  }, [ndviYears, annualYears]);
  const [year, setYear] = useState<number | null>(null);
  useEffect(() => {
    if (availableYears.length && (year === null || !availableYears.includes(year))) {
      setYear(availableYears[availableYears.length - 1]!);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableYears.join(',')]);

  // "season dekads when the archive holds the season, else the annual layer" — approximated
  // here as "the dekad layer has published at least one epoch for the chosen year", the same
  // year-level granularity every other epoch check in this stepper already uses.
  const usesDekads = year !== null && dekadYears.includes(year);
  const rainfallLayerId = usesDekads ? dekadLayerId : annualLayerId;
  const rainfallCaptionKey = usesDekads ? 'intel.uc3.rainfallCaptionDekads' : 'intel.uc3.rainfallCaptionAnnual';
  const [aetiOn, setAetiOn] = useState(false);
  useEffect(() => {
    if (active !== stepNumberOf('rainfall')) setAetiOn(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  const [soilLayer, setSoilLayer] = useState<SoilLayer>('soc');

  /* ---- productivity step: survey yield terciles per watershed, or a modelled layer ---- */
  // `#uc3Measure` is presentation only: the analysis and the results always read the
  // survey yield, whatever this select says.
  const [measure, setMeasure] = useState<Uc3Measure>('survey');

  const ndviStepNum = stepNumberOf('ndvi');
  const activeStepId = stepDefs[active - 1]?.id ?? '';

  /* ---- conditions step: the condition builder ---- */
  const run = useToolRun<ConditionsResult>('conditions');
  const onRunConditions = (clauses: ConditionClause[]) => {
    run.run({ clauses, country, scope: scopeUnit || null, year, crop });
  };
  const resultsStepNum = stepNumberOf('results');
  useEffect(() => {
    if (!run.result) return;
    // Fix round 1: `conditions` names its own layers `conditions:<job_id>[:choropleth]` —
    // an id that never starts with `intel:`, so `resetIntel`/`setIntelUseCase`'s own
    // `results.filter((r) => !r.id.startsWith('intel:'))` cleanup (`lib/state.ts`) could
    // never see it. Re-namespaced under `intel:uc3:` before it ever reaches the store, the
    // same convention every other intel result layer (`intel:uc1:choropleth`,
    // `intel:{uc}:selected`, …) already uses, so a use-case switch or Reset removes it too.
    run.result.result_layers.forEach((spec) =>
      addResultLayer(
        { ...spec, id: `intel:uc3:${spec.id}` },
        { jobId: run.jobId ?? spec.id, tool: 'conditions', label: spec.label },
      ),
    );
    // NOTES #753: the Analyse layers join the flow list; a new run replaces the last one's.
    setUc3StepLayers(
      'conditions',
      run.result.result_layers.map((spec) => ({ layerId: `intel:uc3:${spec.id}`, label: spec.label })),
    );
    setStep(resultsStepNum);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run.result]);

  /**
   * Fix round 1: the results step's selected unit is `useUnitLink('uc3')`'s own `unit` — the
   * same map-click-selects-it wiring `Uc1`/`Uc2` already use — rather than local state of
   * its own, so clicking a highlighted unit *on the map* (a boundary the `conditions`
   * choropleth painted) reaches this pane exactly as picking one from a chart does.
   */
  const { unit: selectedUnit, pick: pickUnit } = useUnitLink('uc3');
  /**
   * Fix wave (launch intelligence, F3c): `uc3/diagnosis` is the whole-programme ranked view
   * design §3.3 describes, not the stepper's own country/scope pick, which only scopes the
   * `conditions` job and its choropleth. Every one of this dev stack's four countries has
   * 2-3 watersheds on its own — scoping the diagnosis to one country would starve the
   * ranking, regardless of which country or watershed the reader picked in step 1.
   */
  const diagnosis = useIntelDiagnosis(crop, year, null);
  const series = useIntelSeries(selectedUnit, year);

  const terciles = useMemo(() => yieldTerciles(diagnosis.data?.ranked ?? []), [diagnosis.data]);

  /* ---- flow layers (client, 8 Oct 2026, NOTES #753) ---- */
  /**
   * What each painting step puts on the map for the current choices (`uc3Layers.ts`):
   * `rasterForStep` for the crop, vegetation, rainfall and soil rasters and the modelled
   * productivity measures; the survey measure is the tercile choropleth `PRODUCTIVITY_LAYER`
   * (once the diagnosis has ranked something). The Analyse layers are written by the run
   * effect above, not here.
   */
  const layerName = (id: string): string => layers.find((l) => l.id === id)?.name ?? id;
  const desiredFor = (stepId: string): { layerId: string; label: string }[] | null => {
    if (stepId === 'indicator' && measure === 'survey') {
      return terciles.size ? [{ layerId: PRODUCTIVITY_LAYER, label: t('intel.uc3.layer.yield', { crop: cropLabel }) }] : null;
    }
    const choice = rasterForStep(stepId, {
      crop: cropDef,
      measure,
      ndviLayerId: 'ndvi_terra',
      rainfallLayerId,
      aetiOn,
      soilLayer,
    });
    if (!choice) return null;
    const label = choice.titleKey
      ? t(choice.titleKey, { crop: cropLabel })
      : stepId === 'ndvi'
        ? `${layerName(choice.layerId)}${year ? `, ${year}` : ''}`
        : layerName(choice.layerId);
    return [{ layerId: choice.layerId, label }];
  };
  const desiredKey = JSON.stringify(PAINTING_STEPS.map((id) => desiredFor(id)));
  /**
   * Entering a painting step adds its layer; every step already on the list follows its
   * own choice (another crop replaces the crop layer and the productivity layer in place).
   * A layer the reader removed with the legend's ✕ stays off until its step is entered
   * again: `entered` only holds steps entered since their layer was last on the list.
   */
  const entered = useRef(new Set<string>());
  const lastActiveStep = useRef<string | null>(null);
  useEffect(() => {
    if (lastActiveStep.current !== activeStepId) {
      lastActiveStep.current = activeStepId;
      if ((PAINTING_STEPS as readonly string[]).includes(activeStepId)) entered.current.add(activeStepId);
    }
    const listed = new Set(useAppStore.getState().intel.uc3Layers.map((l) => l.stepId));
    for (const stepId of PAINTING_STEPS) {
      if (!listed.has(stepId) && !entered.current.has(stepId)) continue;
      const desired = desiredFor(stepId);
      if (!desired) continue;
      setUc3StepLayers(stepId, desired);
      entered.current.delete(stepId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStepId, desiredKey]);

  // The vegetation layer shows the chosen year's latest composite.
  const ndviListed = flowLayers.some((l) => l.layerId === 'ndvi_terra');
  useEffect(() => {
    if (!(ndviListed || active === ndviStepNum) || !ndviSeries) return;
    const inWindow = epochsForYear(ndviSeries, year);
    const target = inWindow[inWindow.length - 1] ?? ndviSeries.epochs?.[ndviSeries.epochs.length - 1];
    if (target) setEpoch('ndvi_terra', epochDate(target));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ndviListed, active, ndviSeries, year]);

  /**
   * The productivity map: survey yield terciles (rank thirds of the programme's ranked
   * watersheds, `yieldTerciles`) from the diagnosis payload the results already read, as a
   * clickable `units` layer with its own class legend. On the map for as long as the flow
   * list names it; `ResultLayers` keeps the watersheds inside the isolated geography.
   */
  const productivityListed = flowLayers.some((l) => l.layerId === PRODUCTIVITY_LAYER);
  useEffect(() => {
    if (!productivityListed || !terciles.size) return;
    const unitIds = [...terciles.keys()];
    addResultLayer(
      {
        id: PRODUCTIVITY_LAYER,
        kind: 'units',
        label: t('intel.uc3.layer.yield', { crop: cropLabel }),
        unit_ids: unitIds,
        colors: Object.fromEntries(unitIds.map((id) => [id, TERCILE_COLORS[terciles.get(id)!]])),
        details: Object.fromEntries(unitIds.map((id) => [id, t(`intel.uc3.tercile.${terciles.get(id)!}`)])),
        classes: TERCILES.map((c) => ({ label: t(`intel.uc3.tercile.${c}`), color: TERCILE_COLORS[c] })),
        // Translucent so the flow's rasters beneath (crop area, vegetation, rainfall) stay
        // readable while the classes are checked in the legend.
        fillOpacity: 0.45,
      },
      { jobId: `uc3-yield-${year}-${crop}`, tool: 'uc3' },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productivityListed, terciles, cropLabel, lang]);

  // Leaving the use case (or closing the dock) clears the flow and the layers it named.
  useEffect(
    () => () => {
      clearUc3Layers();
      removeResultLayer(PRODUCTIVITY_LAYER);
    },
    [clearUc3Layers, removeResultLayer],
  );

  /* ---- the Productivity step's totals for the selected unit (`#uc3ProductionTotal`) ---- */
  const productionUnitId = scopeUnit || (country !== 'ALL' ? countryUnitId : null);
  const productionSums = diagnosis.data?.production?.sums;
  const productionTotal = productionUnitId && productionSums ? (productionSums[productionUnitId] ?? null) : null;
  /**
   * Client, 8 Oct 2026 (NOTES #755): at watershed level the survey yield tile is that
   * watershed's own value and tercile class. At country level it is the SUM of the
   * country's watersheds' survey yield values (those that carry one), at the client's
   * instruction, labelled as a country total with no tercile class. `ranked` is the whole
   * programme's watersheds with a survey yield, so it carries every value the sum needs.
   */
  const unitYieldValues = (diagnosis.data?.ranked ?? [])
    .filter((r) => (scopeUnit ? r.unit_id === scopeUnit : r.country === country))
    .map((r) => r.yield)
    .filter((v): v is number => typeof v === 'number' && Number.isFinite(v));
  const unitYieldKg = unitYieldValues.length ? unitYieldValues.reduce((a, b) => a + b, 0) : null;
  const unitYieldClass = scopeUnit ? (terciles.get(scopeUnit) ?? null) : null;

  const onSelectUnit = (id: string) => {
    const label =
      run.result?.by_unit.find((u) => u.unit_id === id)?.name ??
      diagnosis.data?.ranked.find((p) => p.unit_id === id)?.name;
    void pickUnit(id, label);
  };
  useEffect(() => {
    if (!run.result?.by_unit?.length || selectedUnit) return;
    const top = [...run.result.by_unit].sort((a, b) => (b.share ?? 0) - (a.share ?? 0))[0];
    if (top) void pickUnit(top.unit_id, top.name);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run.result]);

  const countryName =
    country === 'ALL' ? t('intel.focusAllCountries') : t(`country.${country}`, { defaultValue: COUNTRY_NAMES[country] });
  const scopeName = watersheds.data?.find((u) => u.id === scopeUnit)?.name;

  /** A watershed pick, from the select or the map: its own country comes with it. */
  const chooseWatershed = (id: string) => {
    setScopeUnit(id);
    const own = watersheds.data?.find((u) => u.id === id)?.country;
    if (id && own && own !== country && (UC3_COUNTRIES as string[]).includes(own)) setCountry(own as Country);
  };

  // The map stays clickable for re-selection: on the Geography step, a click that resolves
  // to one of the listed watersheds (`useUnitLink`) becomes the selection.
  const geographyStepNum = stepNumberOf('geography');
  useEffect(() => {
    if (active !== geographyStepNum || !selectedUnit || selectedUnit === scopeUnit) return;
    if (watersheds.data?.some((u) => u.id === selectedUnit)) chooseWatershed(selectedUnit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUnit]);

  /* ---- one body renderer per step kind, keyed by the definition's own step id ---- */
  const bodyFor = (def: IntelStepDefinitionRow, n: number): { title: string; summary: ReactNode; body: ReactNode } => {
    switch (def.id) {
      case 'geography':
        return {
          title: t('intel.uc3.step.geography'),
          summary: `${countryName}${scopeName ? `, ${scopeName}` : ''}`,
          body: (
            <div className="ad-checks">
              <label>
                {t('indicators.levelCountry')}
                <select
                  id="uc3Country"
                  value={country}
                  onChange={(e) => {
                    setCountry(e.target.value as Country);
                    setScopeUnit('');
                  }}
                >
                  <option value="ALL">{t('intel.focusAllCountries')}</option>
                  {UC3_COUNTRIES.map((c) => (
                    <option key={c} value={c}>
                      {t(`country.${c}`, { defaultValue: COUNTRY_NAMES[c] })}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                {t('intel.uc3.watershedLabel')}
                <select id="uc3Watershed" value={scopeUnit} onChange={(e) => chooseWatershed(e.target.value)}>
                  <option value="">
                    {country === 'ALL' ? t('intel.focusAllWatersheds') : t('intel.uc3.wholeCountry')}
                  </option>
                  {(watersheds.data ?? []).map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name}
                    </option>
                  ))}
                </select>
              </label>
              <button type="button" className="btn primary sm" onClick={() => setStep(n + 1)}>
                {t('intel.uc3.next')}
              </button>
            </div>
          ),
        };
      case 'crop':
        return {
          title: t('intel.uc3.step.crop'),
          summary: cropLabel,
          body: (
            <div className="ad-checks">
              <select id="uc3Crop" value={crop} onChange={(e) => setCrop(e.target.value)}>
                {crops.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.label[lang] ?? c.id}
                  </option>
                ))}
              </select>
              <button type="button" className="btn primary sm" onClick={() => setStep(n + 1)}>
                {t('intel.uc3.next')}
              </button>
            </div>
          ),
        };
      case 'year':
        return {
          title: t('intel.uc3.step.year'),
          summary: year ?? '-',
          body: (
            <div className="ad-checks">
              <select value={year ?? ''} onChange={(e) => setYear(Number(e.target.value))}>
                {availableYears.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
              <button type="button" className="btn primary sm" onClick={() => setStep(n + 1)}>
                {t('intel.uc3.next')}
              </button>
            </div>
          ),
        };
      case 'indicator':
        return {
          title: t('intel.uc3.step.productivity'),
          summary: t('intel.uc3.productivityLabel', { crop: cropLabel }),
          body: (
            <div className="ad-checks">
              <p className="note">{t('intel.uc3.productivityNote')}</p>
              <label>
                {t('intel.uc3.measureLabel')}
                <select id="uc3Measure" value={measure} onChange={(e) => setMeasure(e.target.value as Uc3Measure)}>
                  {UC3_MEASURES.map((m) => (
                    <option key={m} value={m}>
                      {t(`intel.uc3.measure.${m}`)}
                    </option>
                  ))}
                </select>
              </label>
              {measure !== 'survey' ? <p className="note">{t('intel.uc3.measureReference')}</p> : null}
              {productionUnitId ? (
                <div className="result-box" id="uc3ProductionTotal">
                  <h5>{t('intel.uc3.production.title', { crop: cropLabel, unit: scopeName ?? countryName })}</h5>
                  <div className="result-pad">
                    <StatTiles>
                      <StatTile
                        id="uc3-production-total"
                        value={
                          productionTotal != null
                            ? fmtCompact(productionTotal)
                            : diagnosis.isLoading
                              ? '-'
                              : t('intel.uc3.production.notAvailable')
                        }
                        unit={productionTotal != null ? 't' : undefined}
                        text={productionTotal == null && !diagnosis.isLoading}
                        label={t('intel.uc3.production.total')}
                        sub={t('intel.uc3.production.modelledCaption')}
                      />
                      <StatTile
                        id="uc3-production-yield"
                        value={
                          unitYieldKg != null
                            ? fmtNum(unitYieldKg / 1000, 2)
                            : diagnosis.isLoading
                              ? '-'
                              : t('intel.uc3.production.notAvailable')
                        }
                        unit={unitYieldKg != null ? 't/ha' : undefined}
                        text={unitYieldKg == null && !diagnosis.isLoading}
                        label={scopeUnit ? t('intel.uc3.production.yield') : t('intel.uc3.production.yieldCountryTotal')}
                        sub={
                          scopeUnit
                            ? unitYieldClass
                              ? t(`intel.uc3.production.tercile.${unitYieldClass}`)
                              : undefined
                            : unitYieldValues.length
                              ? t('intel.uc3.production.yieldSumCaption', { n: fmtNum(unitYieldValues.length, 0) })
                              : undefined
                        }
                      />
                    </StatTiles>
                  </div>
                </div>
              ) : null}
              <button type="button" className="btn primary sm" onClick={() => setStep(n + 1)}>
                {t('intel.uc3.next')}
              </button>
            </div>
          ),
        };
      case 'ndvi':
        return {
          title: t('intel.uc3.step.vegetation'),
          summary: t('intel.uc3.step.vegetation'),
          body: (
            <div className="ad-checks">
              <label>
                {t('intel.uc3.opacityLabel')}
                <input
                  id="uc3Opacity"
                  type="range"
                  min={0.1}
                  max={1}
                  step={0.05}
                  value={layerOpacity.ndvi_terra ?? 1}
                  onChange={(e) => setLayerOpacity('ndvi_terra', Number(e.target.value))}
                />
              </label>
              <button type="button" className="btn primary sm" onClick={() => setStep(n + 1)}>
                {t('intel.uc3.next')}
              </button>
            </div>
          ),
        };
      case 'rainfall':
        return {
          title: t('intel.uc3.step.rainfall'),
          summary: t('intel.uc3.step.rainfall'),
          body: (
            <div className="ad-checks">
              <p className="note">{t(rainfallCaptionKey)}</p>
              {aetiDef ? (
                <label>
                  <input
                    type="checkbox"
                    id="uc3Aeti"
                    checked={aetiOn}
                    onChange={(e) => setAetiOn(e.target.checked)}
                  />
                  {t('intel.uc3.aetiLabel')}
                </label>
              ) : null}
              <button type="button" className="btn primary sm" onClick={() => setStep(n + 1)}>
                {t('intel.uc3.next')}
              </button>
            </div>
          ),
        };
      case 'soil':
        return {
          title: t('intel.uc3.step.soil'),
          summary: t(`intel.uc3.layer.${soilLayer}`),
          body: (
            <div className="ad-checks">
              <select id="uc3Soil" value={soilLayer} onChange={(e) => setSoilLayer(e.target.value as SoilLayer)}>
                {SOIL_LAYERS.map((l) => (
                  <option key={l} value={l}>
                    {t(`intel.uc3.layer.${l}`)}
                  </option>
                ))}
              </select>
              <button type="button" className="btn primary sm" onClick={() => setStep(n + 1)}>
                {t('intel.uc3.next')}
              </button>
            </div>
          ),
        };
      case 'conditions':
        return {
          title: t('intel.uc3.step.analyse'),
          summary: t('intel.uc3.analyseSummary'),
          body: (
            <ConditionBuilder
              config={config.data}
              crop={crop}
              cropLabel={cropLabel}
              run={run}
              onRun={onRunConditions}
              blockedNote={country === 'ALL' ? t('intel.uc3.needCountry') : undefined}
            />
          ),
        };
      case 'results':
      default:
        return {
          title: t('intel.uc3.step.results'),
          summary: undefined,
          body: run.result ? (
            <Uc3Results
              result={run.result}
              diagnosis={diagnosis.data}
              series={series.data}
              selectedUnit={selectedUnit}
              onSelectUnit={onSelectUnit}
              lang={lang}
              cropLabel={cropLabel}
            />
          ) : (
            <p className="note">{t('intel.uc3.noResultYet')}</p>
          ),
        };
    }
  };

  const steps: StepperStep[] = stepDefs.map((def, i) => {
    const n = i + 1;
    const { title, summary, body } = bodyFor(def, n);
    return { id: n, title, summary, body };
  });

  return (
    <div className="intel-uc3">
      <Stepper active={active} onSelect={setStep} steps={steps} />
    </div>
  );
}

function Uc3Results({
  result,
  diagnosis,
  series,
  selectedUnit,
  onSelectUnit,
  lang,
  cropLabel,
}: {
  result: ConditionsResult;
  diagnosis?: IntelDiagnosis;
  series?: IntelSeries;
  selectedUnit: string | null;
  onSelectUnit: (id: string) => void;
  lang: 'en' | 'fr';
  cropLabel: string;
}) {
  const { t } = useTranslation();

  const histogramRows = result.by_unit.map((u) => ({
    id: u.unit_id,
    label: u.name,
    value: u.share == null ? null : u.share * 100,
  }));

  // Fix round 1: every read below is guarded — a stale cache entry, a partial payload, or
  // the pre-Task-4 `points`/`correlations` shape a test can still hand this component must
  // fall through to the designed empty state rather than throwing.
  const rankedRows = diagnosis?.ranked ?? [];
  const countryRows = diagnosis?.countries ?? [];
  const statementLines = safeStatements(diagnosis?.statements?.[lang]);
  const point = rankedRows.find((p) => p.unit_id === selectedUnit);
  const meanOf = (factor: (typeof DIAGNOSIS_FACTORS)[number] | 'yield'): number | null => {
    const vals = rankedRows.map((p) => p[factor]).filter((v): v is number => typeof v === 'number');
    return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
  };

  return (
    <div>
      <div className="result-box" id="uc3Stats">
        <h5>{t('intel.uc3.statsTitle')}</h5>
        <div className="result-pad">
          <table className="mini">
            <tbody>
              <tr>
                <td>{t('intel.uc3.stats.area')}</td>
                <td className="num">{fmtNum(result.area_ha, 0)} ha</td>
              </tr>
              <tr>
                <td>{t('intel.uc3.stats.share')}</td>
                <td className="num">
                  {result.share_of_ag_area != null ? `${fmtNum(result.share_of_ag_area * 100, 1)}%` : '-'}
                </td>
              </tr>
              <tr>
                <td>{t('intel.uc3.stats.units')}</td>
                <td className="num">{result.n_units_touched}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="result-box" id="uc3Histogram">
        <h5>{t('intel.uc3.histogramTitle')}</h5>
        <div className="result-pad">
          <ChartFrame
            title={t('intel.uc3.histogramTitle')}
            xLabel={t('intel.axis.matchingShare')}
            yLabel={t('intel.axis.landscape')}
            testId="uc3-histogram"
          >
            {() => (
              <HistogramChart
                rows={histogramRows}
                title={t('intel.uc3.histogramTitle')}
                selectedId={selectedUnit}
                onSelect={onSelectUnit}
              />
            )}
          </ChartFrame>
        </div>
      </div>

      {/*
       * Amendment 1 §3.3: no correlation coefficient anywhere — the ranked bar chart and the
       * server's own rank-based `statements[]` replace the old scatter/ρ block outright.
       * Always rendered in the results step, same "designed empty state, never a silently
       * missing chart" rule the old block followed.
       */}
      <div className="result-box" id="uc3Ranked">
        <h5>{t('intel.uc3.rankedTitle')}</h5>
        <div className="result-pad">
          {rankedRows.length ? (
            <>
              {diagnosis?.season && diagnosis.year ? (
                <p className="sub">{t('intel.uc3.rankedPeriod', { season: diagnosis.season, year: diagnosis.year })}</p>
              ) : null}
              <ChartFrame
                title={t('intel.uc3.rankedTitle')}
                subtitle={diagnosis?.season && diagnosis.year ? t('intel.uc3.rankedPeriod', { season: diagnosis.season, year: diagnosis.year }) : undefined}
                xLabel={axisTitle(t('intel.charts.yield', { crop: cropLabel }), DIAGNOSIS_UNITS.yield)}
                yLabel={t('charts.axis.watershed')}
                testId="uc3-ranked"
              >
                {() => (
                  <RankedBarChart
                    rows={rankedRows}
                    season={diagnosis?.season ?? ''}
                    year={diagnosis?.year ?? 0}
                    yieldLabel={t('intel.charts.yield', { crop: cropLabel })}
                    modelledLabel={t('intel.uc3.modelledColumnLabel')}
                    ndviLabel={t('intel.charts.factor.ndvi_peak')}
                    rainLabel={t('intel.charts.factor.rain')}
                    socLabel={t('intel.charts.factor.soc')}
                    yieldUnit={DIAGNOSIS_UNITS.yield}
                    modelledUnit={DIAGNOSIS_UNITS.modelled_yield}
                    ndviUnit={DIAGNOSIS_UNITS.ndvi_peak}
                    rainUnit={DIAGNOSIS_UNITS.rain}
                    socUnit={DIAGNOSIS_UNITS.soc}
                    selectedId={selectedUnit}
                    onSelect={onSelectUnit}
                  />
                )}
              </ChartFrame>
              {statementLines.length ? (
                <ul id="uc3Statements" className="tbl-note">
                  {statementLines.map((line, i) => (
                    <li key={i}>{line}</li>
                  ))}
                </ul>
              ) : null}
            </>
          ) : (
            <p className="ad-status">{t('intel.uc3.noRankedData')}</p>
          )}
        </div>
      </div>

      {/*
       * Amendment 1 fix wave (F12): the diagnosis payload's `countries[]` — the same four
       * columns as the ranked chart, one row per programme country instead of per watershed
       * (no `rank`) — shown as its own titled block right below the ranked chart. Same
       * "designed empty state, never a silently missing block" rule as `uc3Ranked`.
       */}
      <div className="result-box" id="uc3Countries">
        <h5>{t('intel.uc3.countryRollupTitle')}</h5>
        <div className="result-pad">
          {countryRows.length ? (
            <>
              {diagnosis?.season && diagnosis.year ? (
                <p className="sub">{t('intel.uc3.rankedPeriod', { season: diagnosis.season, year: diagnosis.year })}</p>
              ) : null}
              <table className="mini" id="uc3CountriesTable">
                <thead>
                  <tr>
                    <th>{t('intel.uc3.countryRollupCountry')}</th>
                    {(['yield', ...DIAGNOSIS_FACTORS] as const).map((f) => (
                      <th className="num" key={f}>
                        {t(`intel.charts.factor.${f}`, { defaultValue: f, crop: cropLabel })} ({DIAGNOSIS_UNITS[f]})
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {countryRows.map((c) => (
                    <tr key={c.unit_id}>
                      <td>{c.name}</td>
                      {(['yield', ...DIAGNOSIS_FACTORS] as const).map((f) => (
                        <td className="num" key={f}>
                          {typeof c[f] === 'number' ? fmtNum(c[f] as number, 1) : '-'}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          ) : (
            <p className="ad-status">{t('intel.uc3.noCountryData')}</p>
          )}
        </div>
      </div>

      <div className="result-box" id="uc3Series">
        <h5>{t('intel.uc3.seriesTitle')}</h5>
        <div className="result-pad">
          {!selectedUnit ? (
            <p className="ad-status">{t('intel.uc3.noSeriesUnit')}</p>
          ) : series && series.ndvi.length === 0 && series.rain.length === 0 ? (
            <p className="ad-status">{t('intel.uc3.noSeriesData')}</p>
          ) : series ? (
            <ChartFrame
              title={t('intel.uc3.seriesTitle')}
              xLabel={t('intel.axis.period')}
              yLabel={t('intel.axis.ndviRain')}
              testId="uc3-series"
            >
              {() => (
                <SeriesChart
                  ndvi={series.ndvi}
                  rain={series.rain}
                  ndviLabel={t('intel.charts.ndvi')}
                  rainLabel={t('intel.charts.rain')}
                />
              )}
            </ChartFrame>
          ) : null}
        </div>
      </div>

      {diagnosis?.insight ? (
        <Insight
          insight={{
            en: safeInsightText(diagnosis.insight.en, t('intel.uc3.insightUnavailable')),
            fr: safeInsightText(diagnosis.insight.fr, t('intel.uc3.insightUnavailable', { lng: 'fr' })),
          }}
        />
      ) : null}

      {point ? (
        <div className="result-box" id="uc3UnitVsCountry">
          <h5>{point.name}</h5>
          <div className="result-pad">
            <table className="mini">
              <thead>
                <tr>
                  <th />
                  <th className="num">{point.name}</th>
                  <th className="num">{t('intel.uc3.countryMean')}</th>
                </tr>
              </thead>
              <tbody>
                {(['yield', ...DIAGNOSIS_FACTORS] as const).map((f) => (
                  <tr key={f}>
                    <td>{t(`intel.charts.factor.${f}`, { defaultValue: f, crop: cropLabel })}</td>
                    <td className="num">
                      {typeof point[f] === 'number' ? fmtNum(point[f] as number, 1) : '-'}
                    </td>
                    <td className="num">
                      {meanOf(f) != null ? fmtNum(meanOf(f) as number, 1) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default Uc3;
